import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, HelpCircle, UserPlus, ShieldCheck, FileText, BadgeCheck, ShoppingCart, CalendarClock, AlertTriangle, Activity, Send, MessageSquare, TrendingUp, Gift, CalendarCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { badge, ukDate } from "./crmShared";
import { gbp } from "./crmPipelineShared";

const inv = (status) => `/crm/investors?status=${encodeURIComponent(status)}`;
const CARDS = [
  { key: "new_leads", label: "New Investor Leads", icon: Inbox, tone: "text-blue-300", to: inv("New Lead") },
  { key: "awaiting_qualification", label: "Awaiting Qualification", icon: HelpCircle, tone: "text-sky-300", to: inv("Contacted,Initial Qualification,Qualified") },
  { key: "awaiting_onboarding", label: "Awaiting Onboarding", icon: UserPlus, tone: "text-indigo-300", to: inv("Invited to Onboard,£40 Onboarding Charge Pending,Onboarding Charge Paid") },
  { key: "aml_pending", label: "AML / KYC Pending", icon: ShieldCheck, tone: "text-violet-300", to: inv("AML/KYC Pending") },
  { key: "terms_pending", label: "Investor Terms Pending", icon: FileText, tone: "text-fuchsia-300", to: inv("Investor Terms Pending") },
  { key: "approved", label: "Approved Investors", icon: BadgeCheck, tone: "text-emerald-300", to: inv("Approved Investor") },
  { key: "active_buyers", label: "Active Buyers", icon: ShoppingCart, tone: "text-teal-300", to: inv("Active Buyer") },
  { key: "followups_today", label: "Follow-ups Due Today", icon: CalendarClock, tone: "text-amber-300", to: "/crm/investors?due=today" },
  { key: "overdue_followups", label: "Overdue Follow-ups", icon: AlertTriangle, tone: "text-orange-300", to: "/crm/investors?due=overdue" },
  { key: "deals_ready_for_distribution", label: "Deals Ready to Distribute", icon: Send, tone: "text-teal-300", to: "/crm/properties" },
  { key: "investor_responses_required", label: "Responses Required", icon: MessageSquare, tone: "text-sky-300", to: "/crm/properties" },
  { key: "active_protected_introductions", label: "Active Introductions", icon: ShieldCheck, tone: "text-emerald-300", to: "/crm/properties" },
  { key: "investors_proceeding", label: "Investors Proceeding", icon: TrendingUp, tone: "text-brass", to: inv("Active Buyer") },
];

export default function CrmDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rstats, setRstats] = useState(null);
  const [fu, setFu] = useState(null);
  const [conv, setConv] = useState(null);

  useEffect(() => {
    api.get("/crm/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Could not load dashboard"));
    api.get("/crm/resource-stats").then((r) => setRstats(r.data)).catch(() => {});
    api.get("/crm/followup-summary").then((r) => setFu(r.data)).catch(() => {});
    api.get("/crm/conversion-analytics").then((r) => setConv(r.data)).catch(() => {});
  }, []);

  const cards = data?.cards || {};

  const bump = async (e, invId, days) => {
    e.stopPropagation();
    try { await api.post(`/crm/investors/${invId}/followup`, { days }); toast.success("Follow-up updated"); const r = await api.get("/crm/dashboard"); setData(r.data); }
    catch { toast.error("Could not update follow-up"); }
  };

  return (
    <CrmShell title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {CARDS.map((c) => (
          <button key={c.key} onClick={() => navigate(c.to)} data-testid={`crm-card-${c.key.replace(/_/g, "-")}`}
            className="text-left bg-[#0F2E23] rounded-xl border border-white/10 p-4 hover:border-brass/40 transition">
            <div className="flex items-center justify-between"><span className="text-xs text-emerald-100/60">{c.label}</span><c.icon className={`w-4 h-4 ${c.tone}`} /></div>
            <div className="mt-2 text-2xl font-mono font-semibold text-white">{data ? (cards[c.key] ?? 0) : "—"}</div>
          </button>
        ))}
      </div>

      <button onClick={() => navigate("/crm/resources")} data-testid="crm-card-resources" className="w-full text-left bg-gradient-to-r from-[#0F2E23] to-[#123a2b] rounded-xl border border-brass/25 p-5 mb-8 hover:border-brass/60 transition">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3"><Gift className="w-5 h-5 text-brass" /><div><div className="text-sm font-semibold text-white">Resources / Lead Magnet</div><div className="text-xs text-emerald-100/60">Free resource downloads & marketing opt-ins</div></div></div>
          <div className="flex gap-6 text-center">
            {[["Leads", rstats?.total_resource_leads], ["Investor", rstats?.investor_leads], ["Vendor", rstats?.vendor_leads], ["Subscribers", rstats?.subscribers], ["This month", rstats?.downloads_this_month]].map(([l, v]) => (
              <div key={l}><div className="text-xl font-mono font-semibold text-white">{rstats ? (v ?? 0) : "—"}</div><div className="text-[10px] uppercase text-emerald-100/50">{l}</div></div>
            ))}
          </div>
        </div>
      </button>

      {/* Follow-up System widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="crm-followup-widgets">
        {[
          { key: "due_today", label: "Due Today", icon: CalendarClock, tone: "text-amber-300" },
          { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "text-orange-300" },
          { key: "appointments", label: "Upcoming Appointments", icon: CalendarCheck, tone: "text-teal-300" },
          { key: "new_leads", label: "New Leads (48h)", icon: Sparkles, tone: "text-blue-300" },
        ].map((w) => (
          <div key={w.key} data-testid={`fu-widget-${w.key}`} className="bg-[#0F2E23] rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between"><span className="text-xs text-emerald-100/60">{w.label}</span><w.icon className={`w-4 h-4 ${w.tone}`} /></div>
            <div className="mt-2 text-2xl font-mono font-semibold text-white">{fu ? (fu.counts?.[w.key] ?? 0) : "—"}</div>
          </div>
        ))}
      </div>

      {fu && (fu.overdue.length > 0 || fu.due_today.length > 0 || fu.appointments.length > 0 || fu.new_leads.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mb-8">
          {[
            { key: "overdue", title: "Overdue follow-ups", icon: AlertTriangle, dateField: "next_follow_up" },
            { key: "due_today", title: "Due today", icon: CalendarClock, dateField: "next_follow_up" },
            { key: "appointments", title: "Upcoming appointments", icon: CalendarCheck, dateField: "appointment_at" },
            { key: "new_leads", title: "New leads (48h)", icon: Sparkles, dateField: "next_follow_up" },
          ].map((col) => (
            <div key={col.key} className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid={`fu-list-${col.key}`}>
              <div className="flex items-center gap-2 mb-3"><col.icon className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">{col.title}</h3><span className="text-[11px] text-emerald-100/40">{fu[col.key].length}</span></div>
              {fu[col.key].length === 0 && <p className="text-emerald-100/40 text-xs py-4 text-center">Nothing here.</p>}
              <ul className="divide-y divide-white/5">
                {fu[col.key].slice(0, 8).map((c) => (
                  <li key={`${c.store}-${c.id}`} onClick={() => navigate(`/crm/contact/${c.store}/${c.id}`)} className="flex items-center justify-between py-2 cursor-pointer hover:bg-white/5 -mx-2 px-2 rounded-lg">
                    <div><div className="text-white text-sm">{c.name}</div><div className="text-[11px] text-emerald-100/50">{c.contact_type} · {ukDate(c[col.dateField])}</div></div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-emerald-100/70">{c.pipeline_stage || "New Lead"}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {conv && (
        <div className="mb-8" data-testid="crm-conversion-analytics">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">Conversion & Revenue</h3></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[["Deals Completed", conv.total_completed, false, "completed"], ["Fee Revenue", conv.total_fee_revenue, true, "fee"], ["Fee This Month", conv.fee_this_month, true, "fee-month"], ["Total Deal Value", conv.total_deal_value, true, "value"]].map(([l, v, money, k]) => (
              <div key={k} data-testid={`conv-stat-${k}`} className="bg-[#0F2E23] rounded-xl border border-white/10 p-4">
                <div className="text-xs text-emerald-100/60">{l}</div>
                <div className="mt-2 text-2xl font-mono font-semibold text-white">{money ? gbp(v) : v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[["By source", conv.by_source, "source"], ["By type", conv.by_type, "type"], ["By month", conv.by_month, "month"]].map(([title, rows, k]) => (
              <div key={k} data-testid={`conv-breakdown-${k}`} className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
                <h4 className="text-xs font-semibold text-white mb-3">{title}</h4>
                {rows.length === 0 && <p className="text-emerald-100/40 text-xs py-4 text-center">No completed deals yet.</p>}
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.key} className="border-b border-white/5">
                        <td className="py-1.5 text-emerald-100/70">{r.key}</td>
                        <td className="py-1.5 text-right text-emerald-100/50 text-xs">{r.count}</td>
                        <td className="py-1.5 text-right text-emerald-300 font-mono">{gbp(r.fee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="crm-attention">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-amber-300" /><h3 className="text-sm font-semibold text-white">Investors requiring attention</h3></div>
          {data && data.attention.length === 0 && <p className="text-emerald-100/50 text-sm py-6 text-center">No follow-ups due or overdue. All caught up.</p>}
          <ul className="divide-y divide-white/5">
            {(data?.attention || []).map((a) => (
              <li key={a.id} onClick={() => navigate(`/crm/investors/${a.id}`)} data-testid={`attention-${a.id}`} className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-white/5 -mx-2 px-2 rounded-lg">
                <div><div className="text-white text-sm">{a.name || "Unnamed"}</div><div className="text-[11px] text-emerald-100/50">Follow-up {ukDate(a.next_follow_up)}</div></div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => bump(e, a.id, 3)} data-testid={`bump-3d-${a.id}`} className="text-[10px] bg-white/5 hover:bg-white/15 text-emerald-100/80 rounded-md px-2 py-0.5">+3d</button>
                  <button onClick={(e) => bump(e, a.id, 7)} data-testid={`bump-1w-${a.id}`} className="text-[10px] bg-white/5 hover:bg-white/15 text-emerald-100/80 rounded-md px-2 py-0.5">+1w</button>
                  {a.overdue && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">Overdue</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(a.pipeline_status)}`}>{a.pipeline_status}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="crm-recent-activity">
          <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">Recent investor activity</h3></div>
          {data && data.recent_activity.length === 0 && <p className="text-emerald-100/50 text-sm py-6 text-center">No activity yet.</p>}
          <ol className="space-y-2.5">
            {(data?.recent_activity || []).map((a) => (
              <li key={a.id} onClick={() => a.investor_id && navigate(`/crm/investors/${a.investor_id}`)} className="flex gap-3 text-sm cursor-pointer hover:bg-white/5 -mx-2 px-2 py-1 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-brass mt-1.5 shrink-0" />
                <div><div className="text-white">{a.investor_name} <span className="text-emerald-100/60">— {a.type}{a.note ? `: ${a.note}` : ""}</span></div>
                  <div className="text-[11px] text-emerald-100/40">{new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}</div></div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </CrmShell>
  );
}
